import React, { useState, useEffect } from 'react'
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  updateDoc,
  addDoc,
  serverTimestamp
} from 'firebase/firestore'
import { db } from '../../firebase'
import SwapWrestler from './SwapWrestler'
import '../../styles/FactionRosterExperience.css'
import {
  buildFactionMemberFields,
  getFactionStoredWrestlers
} from '../../utils/factionWrestlers'
import { buildFactionLineupUpdateAfterSwap } from '../../utils/factionLineup'
import { getFactionMemberSwapSummary } from '../../utils/factionScoring'
import {
  getRosterAvailableWrestlers,
  isWrestlerActive,
  isWrestlerAllowedByRosterRules
} from '../../utils/wrestlerAvailability'
import CreditIcon from '../CreditIcon'

export default function WrestlerAuction ({ factionId, onAuctionUpdate }) {
  const [faction, setFaction] = useState(null)
  const [allWrestlers, setAllWrestlers] = useState([])
  const [availableWrestlers, setAvailableWrestlers] = useState([])
  const [selectedWrestlerToRelease, setSelectedWrestlerToRelease] = useState('')
  const [selectedWrestlerToBuy, setSelectedWrestlerToBuy] = useState('')
  const [auctionPrice, setAuctionPrice] = useState('')
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('release') // 'release', 'acquire', or 'swap'
  const [roster, setRoster] = useState(null)

  // Funzione per aggiornare i dati locali dopo operazioni
  const updateLocalData = async (updatedFaction = null) => {
    try {
      let factionData
      if (updatedFaction) {
        // Usa i dati aggiornati passati come parametro
        factionData = updatedFaction
        setFaction(updatedFaction)
      } else {
        // Ricarica i dati della fazione da Firestore
        const factionDoc = await getDoc(doc(db, 'factions', factionId))
        if (factionDoc.exists()) {
          factionData = { id: factionDoc.id, ...factionDoc.data() }
          setFaction(factionData)
        }
      }

      if (!factionData) return

      let rosterData = null
      if (factionData.rosterId) {
        const rosterSnap = await getDoc(doc(db, 'rosters', factionData.rosterId))
        rosterData = rosterSnap.exists()
          ? { id: rosterSnap.id, ...rosterSnap.data() }
          : null
        setRoster(rosterData)
      }

      // Ricarica le fazioni del roster per aggiornare le liste
      const factionsQuery = factionData.rosterId
        ? query(
            collection(db, 'factions'),
            where('rosterId', '==', factionData.rosterId)
          )
        : query(
            collection(db, 'factions'),
            where('roster', '==', factionData.roster)
          )
      const factionsSnap = await getDocs(factionsQuery)
      const factionsData = factionsSnap.docs.map(d => ({
        id: d.id,
        ...d.data()
      }))

      const available = getRosterAvailableWrestlers({
        wrestlers: allWrestlers,
        factions: factionsData,
        onlyMainRoster: Boolean(rosterData?.onlyMainRoster),
        onlyNXT: Boolean(rosterData?.onlyNXT)
      })
      setAvailableWrestlers(available)
    } catch (error) {
      console.error('Errore aggiornamento dati locali:', error)
    }
  }

  const refreshFactionData = async () => {
    try {
      const factionDoc = await getDoc(doc(db, 'factions', factionId))
      if (factionDoc.exists()) {
        const factionData = { id: factionDoc.id, ...factionDoc.data() }
        // Usa updateLocalData con i nuovi dati
        updateLocalData(factionData)
      }
    } catch (error) {
      console.error('Errore aggiornamento fazione:', error)
    }
  }

  // Carica tutti i dati necessari
  useEffect(() => {
    const loadData = async () => {
      try {
        // 1. Carica fazione corrente
        const factionDoc = await getDoc(doc(db, 'factions', factionId))
        if (!factionDoc.exists()) return
        const factionData = factionDoc.data()
        setFaction({ id: factionDoc.id, ...factionData })
        let rosterData = null
        if (factionData.rosterId) {
          const rosterSnap = await getDoc(doc(db, 'rosters', factionData.rosterId))
          rosterData = rosterSnap.exists()
            ? { id: rosterSnap.id, ...rosterSnap.data() }
            : null
          setRoster(rosterData)
        }

        // 2. Carica tutti i wrestler
        const wrestlersSnap = await getDocs(collection(db, 'wrestlers'))
        const wrestlersData = wrestlersSnap.docs.map(d => ({
          id: d.id,
          ...d.data()
        }))
        setAllWrestlers(wrestlersData)

        // 3. Carica tutte le fazioni del roster corrente
        const factionsQuery = factionData.rosterId
          ? query(
              collection(db, 'factions'),
              where('rosterId', '==', factionData.rosterId)
            )
          : query(
              collection(db, 'factions'),
              where('roster', '==', factionData.roster)
            )
        const factionsSnap = await getDocs(factionsQuery)
        const factionsData = factionsSnap.docs.map(d => ({
          id: d.id,
          ...d.data()
        }))

        // Filtra wrestler attivi e non assegnati a nessuna fazione del roster.
        const available = getRosterAvailableWrestlers({
          wrestlers: wrestlersData,
          factions: factionsData,
          onlyMainRoster: Boolean(rosterData?.onlyMainRoster),
          onlyNXT: Boolean(rosterData?.onlyNXT)
        })
        setAvailableWrestlers(available)
      } catch (error) {
        console.error('Errore caricamento dati asta:', error)
        setMessage('Errore nel caricamento dei dati')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [factionId])

  // Calcola crediti dopo operazioni
  const calculateCreditsAfterRelease = () => {
    if (!faction || !selectedWrestlerToRelease) return faction?.creditsLeft || 0

    const wrestlerToRelease = getFactionStoredWrestlers(faction).find(
      c => c.id === selectedWrestlerToRelease
    )
    if (!wrestlerToRelease) return faction.creditsLeft

    return faction.creditsLeft + wrestlerToRelease.cost
  }

  // Svincola wrestler
  const handleReleaseWrestler = async () => {
    if (!selectedWrestlerToRelease) {
      setMessage('Seleziona un wrestler da svincolare')
      return
    }

    try {
      const wrestlerToRelease = getFactionStoredWrestlers(faction).find(
        c => c.id === selectedWrestlerToRelease
      )
      if (!wrestlerToRelease) {
        setMessage('Wrestler non trovato nella fazione')
        return
      }

      // USIAMO IL COSTO ORIGINALE DELL'ACQUISTO (campo 'cost' nel record della fazione)
      const refundAmount = wrestlerToRelease.cost
      const wrestlerData = allWrestlers.find(
        w => w.id === selectedWrestlerToRelease
      )
      const swapSummary = wrestlerData
        ? getFactionMemberSwapSummary({
          faction,
          roster,
          wrestlerId: selectedWrestlerToRelease,
          currentScore: wrestlerData.score || 0,
          startScore: wrestlerToRelease.startScore || 0,
          accumulatedPoints: wrestlerToRelease.accumulatedPoints || 0,
          status: wrestlerToRelease.status || null
        })
        : { currentPoints: 0, totalPoints: 0, savedPoints: 0 }
      const newAccumulatedPoints = (faction.accumulatedPoints || 0) + swapSummary.currentPoints
      const newSumPoints = (faction.sumPoints || 0) + swapSummary.savedPoints

      // Rimuovi wrestler dalla fazione
      const updatedWrestlers = getFactionStoredWrestlers(faction).filter(
        c => c.id !== selectedWrestlerToRelease
      )
      const lineupUpdate = buildFactionLineupUpdateAfterSwap({
        faction,
        roster,
        updatedWrestlers,
        now: new Date()
      })
      const newCredits = faction.creditsLeft + refundAmount

      await updateDoc(doc(db, 'factions', factionId), {
        ...buildFactionMemberFields(updatedWrestlers),
        ...(lineupUpdate || {}),
        creditsLeft: newCredits,
        accumulatedPoints: newAccumulatedPoints,
        sumPoints: newSumPoints,
        updatedAt: serverTimestamp()
      })

      // Registra l'operazione di svincolo
      await addDoc(collection(db, 'factions', factionId, 'swaps'), {
        wrestlerOut: doc(db, 'wrestlers', selectedWrestlerToRelease),
        wrestlerIn: null,
        scoreOut: swapSummary.savedPoints,
        currentPointsOut: swapSummary.currentPoints,
        accumulatedPointsOut: wrestlerToRelease.accumulatedPoints || 0,
        totalPointsOut: swapSummary.totalPoints,
        sumPointsOut: swapSummary.savedPoints,
        scoreIn: 0,
        priceOut: wrestlerToRelease.cost,
        priceIn: 0,
        date: serverTimestamp(),
        operationType: 'release',
        wrestlerOutName: wrestlerData?.name || 'Sconosciuto'
      })

      // Aggiorna i dati locali immediatamente
      setFaction(prev => ({
        ...prev,
        ...buildFactionMemberFields(updatedWrestlers),
        ...(lineupUpdate || {}),
        creditsLeft: newCredits,
        accumulatedPoints: newAccumulatedPoints,
        sumPoints: newSumPoints
      }))

      // Aggiorna le liste locali
      const releasedWrestler = allWrestlers.find(
        w => w.id === selectedWrestlerToRelease
      )
      if (
        releasedWrestler &&
        isWrestlerActive(releasedWrestler.active) &&
        isWrestlerAllowedByRosterRules(releasedWrestler, {
          onlyMainRoster: Boolean(roster?.onlyMainRoster),
          onlyNXT: Boolean(roster?.onlyNXT)
        })
      ) {
        setAvailableWrestlers(prev =>
          prev.some(wrestler => wrestler.id === releasedWrestler.id)
            ? prev
            : [...prev, releasedWrestler]
        )
      }

      setMessage(
        `Wrestler svincolato! Guadagnati ${refundAmount}$ crediti e conservati ${swapSummary.savedPoints} punti`
      )
      setSelectedWrestlerToRelease('')
    } catch (error) {
      console.error('Errore svincolo wrestler:', error)
      setMessage('Errore durante lo svincolo')
    }
  }

  // Acquista wrestler
  const handleBuyWrestler = async () => {
    if (!selectedWrestlerToBuy) {
      setMessage('Seleziona un wrestler da acquistare')
      return
    }

    if (!auctionPrice || Number(auctionPrice) <= 0) {
      setMessage("Inserisci un prezzo valido per l'asta")
      return
    }

    const creditsAfterRelease = calculateCreditsAfterRelease()
    const wrestlerToBuy = availableWrestlers.find(
      w => w.id === selectedWrestlerToBuy
    )
    if (!wrestlerToBuy) {
      setMessage('Wrestler non trovato')
      return
    }

    const buyPrice = Number(auctionPrice)

    if (faction.creditsLeft < buyPrice) {
      setMessage("Crediti insufficienti per l'acquisto")
      return
    }

    try {
      // Aggiungi wrestler alla fazione
      const newCost = {
        cost: buyPrice,
        startScore: wrestlerToBuy.score || 0 // Inizializza lo startScore
      }

      const updatedWrestlers = [
        ...getFactionStoredWrestlers(faction),
        { ...newCost, id: selectedWrestlerToBuy }
      ]
      const lineupUpdate = buildFactionLineupUpdateAfterSwap({
        faction,
        roster,
        updatedWrestlers,
        now: new Date()
      })
      const newCredits = creditsAfterRelease - buyPrice

      await updateDoc(doc(db, 'factions', factionId), {
        ...buildFactionMemberFields(updatedWrestlers),
        ...(lineupUpdate || {}),
        creditsLeft: newCredits,
        updatedAt: serverTimestamp()
      })

      // Registra l'acquisto nella collezione swaps (compatibile con SwapWrestler)
      await addDoc(collection(db, 'factions', factionId, 'swaps'), {
        wrestlerOut: null, // Compatibile con SwapResume
        wrestlerIn: doc(db, 'wrestlers', selectedWrestlerToBuy),
        scoreOut: 0,
        scoreIn: wrestlerToBuy.score || 0,
        priceOut: 0,
        priceIn: buyPrice,
        date: serverTimestamp(),
        operationType: 'purchase'
      })

      setMessage(`Wrestler acquistato all'asta! Spesi ${buyPrice}$ crediti`)
      setSelectedWrestlerToBuy('')
      setAuctionPrice('')

      // Aggiorna i dati locali immediatamente
      setFaction(prev => ({
        ...prev,
        ...buildFactionMemberFields(updatedWrestlers),
        ...(lineupUpdate || {}),
        creditsLeft: newCredits
      }))

      // Aggiorna le liste locali
      const boughtWrestler = allWrestlers.find(
        w => w.id === selectedWrestlerToBuy
      )
      if (boughtWrestler) {
        setAvailableWrestlers(prev =>
          prev.filter(w => w.id !== selectedWrestlerToBuy)
        )
      }

      await refreshFactionData()
      setMessage(`Wrestler acquistato all'asta! Spesi ${buyPrice}$ crediti`)
    } catch (error) {
      console.error('Errore acquisto wrestler:', error)
      setMessage("Errore durante l'acquisto")
    }
  }

  if (loading)
    return (
      <div className='frx-empty'>
        <div className='spinner-border text-brand-secondary'></div>
        <p className='mb-0'>Caricamento mercato...</p>
      </div>
    )
  if (!faction)
    return (
      <div className='frx-empty'>
        <i className='bi bi-exclamation-circle display-6'></i>
        <p className='mb-0'>Fazione non trovata.</p>
      </div>
    )

  const creditsAfterRelease = calculateCreditsAfterRelease()
  return (
    <div className='card shadow-sm frx-surface wrestler-auction'>
      <button
        type='button'
        className='btn btn-sm btn-outline-light'
        onClick={() => {
          setLoading(true)
          if (onAuctionUpdate) {
            onAuctionUpdate()
          }
          setTimeout(() => {
            setLoading(false)
          }, 1000)
        }}
        title='Ricarica dati'
      >
        <i className='bi bi-arrow-clockwise'></i>
      </button>
      <div className='card-body'>
        <div
          className='frx-summary-grid mb-4'
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem'
          }}
        >
          <div
            className='frx-summary-card'
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem'
            }}
          >
              <i className='bi bi-people-fill fs-4'></i>
            <div style={{ flex: 1 }}>
              <span className='frx-label d-block'>Wrestler in fazione</span>
              <div
                className='frx-value'
                style={{ fontSize: '1.5rem', fontWeight: 'bold' }}
              >
                {getFactionStoredWrestlers(faction)?.length || 0}
              </div>
            </div>
          </div>

          <div
            className='frx-summary-card'
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem'
            }}
          >
              <CreditIcon className='me-1 text-money' size={25} />
            <div style={{ flex: 1 }}>
              <span className='frx-label d-block'>Crediti residui</span>
              <div
                className='frx-value'
                style={{ fontSize: '1.5rem', fontWeight: 'bold' }}
              >
                {faction.creditsLeft}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs responsive */}
        <div className='d-flex justify-content-center mb-3'>
          <ul
            className='frx-tab-nav'
            style={{ listStyle: 'none', paddingLeft: 0, marginBottom: 0 }}
          >
            <li>
              <button
                className={`frx-tab-button ${
                  activeTab === 'release' ? 'active is-active' : ''
                }`}
                onClick={() => setActiveTab('release')}
                aria-label='Svincola'
              >
                <i className='bi bi-box-arrow-right'></i>
                <span className='tab-text'>Svincola</span>
              </button>
            </li>
            <li>
              <button
                className={`frx-tab-button ${
                  activeTab === 'acquire' ? 'active is-active' : ''
                }`}
                onClick={() => setActiveTab('acquire')}
                aria-label='Acquista'
              >
                <i className='bi bi-box-arrow-in-left'></i>
                <span className='tab-text'>Acquista</span>
              </button>
            </li>
            <li>
              <button
                className={`frx-tab-button ${
                  activeTab === 'swap' ? 'active is-active' : ''
                }`}
                onClick={() => setActiveTab('swap')}
                aria-label='Scambia'
              >
                <i className='bi bi-arrow-left-right'></i>
                <span className='tab-text'>Scambia</span>
              </button>
            </li>
          </ul>
        </div>

        <style>{`
  /* Stili per dispositivi mobili */
  @media (max-width: 768px) {
    .nav-tabs-responsive {
      width: 100%;
      overflow-x: auto;
      white-space: nowrap;
      -webkit-overflow-scrolling: touch;
    }
    
    .nav-tabs-responsive .nav-item {
      display: inline-block;
      float: none;
    }
    
    .nav-tabs-responsive .nav-link {
      padding: 0.5rem;
    }
    
    .nav-tabs-responsive .tab-text {
      display: none;
    }
    
    .nav-tabs-responsive .nav-link.active .tab-text,
    .nav-tabs-responsive .nav-link:focus .tab-text,
    .nav-tabs-responsive .nav-link:hover .tab-text {
      display: inline;
      margin-left: 0.25rem;
    }
    
    .nav-tabs-responsive .nav-link {
      transition: all 0.3s ease;
    }
  }
  
  /* Stili per desktop */
  @media (min-width: 769px) {
    .nav-tabs-responsive .tab-text {
      display: inline;
      margin-left: 0.25rem;
    }
  }
`}</style>

        {/* Tab content */}
        <div className='tab-content'>
          {/* Tab Svincoli */}
          <div
            className={`tab-pane fade ${
              activeTab === 'release' ? 'show active' : ''
            }`}
          >
            <div className='card border-danger frx-auction-card'>
              <div className='card-header bg-danger text-white'>
                <h6 className='mb-0'>
                  <i className='bi bi-box-arrow-right me-2'></i>Svincola
                  Wrestler
                </h6>
              </div>
              <div className='card-body'>
                <div className='mb-3'>
                  <select
                    className='form-select'
                    value={selectedWrestlerToRelease}
                    onChange={e => setSelectedWrestlerToRelease(e.target.value)}
                  >
                    <option value=''>Seleziona wrestler...</option>
                    {getFactionStoredWrestlers(faction)?.map(cost => {
                      const wrestler = allWrestlers.find(w => w.id === cost.id)
                      return (
                        <option key={cost.id} value={cost.id}>
                          {wrestler?.name || 'Sconosciuto'} ({cost.cost}$)
                        </option>
                      )
                    })}
                  </select>
                </div>

                {selectedWrestlerToRelease && (
                  <div className='alert alert-success frx-alert bg-success bg-opacity-10 text-light'>
                    <div className='d-flex justify-content-between'>
                      <span>Crediti guadagnati:</span>
                      <strong>
                        +
                        {getFactionStoredWrestlers(faction).find(
                          c => c.id === selectedWrestlerToRelease
                        )?.cost || 0}
                        $
                      </strong>
                    </div>
                  </div>
                )}

                <button
                  type='button'
                  className='btn btn-outline-danger w-100'
                  onClick={handleReleaseWrestler}
                  disabled={!selectedWrestlerToRelease}
                >
                  <i className='bi bi-box-arrow-right me-2'></i>Svincola
                  Wrestler
                </button>
              </div>
            </div>
          </div>

          {/* Tab Acquisti */}
          <div
            className={`tab-pane fade ${
              activeTab === 'acquire' ? 'show active' : ''
            }`}
          >
            <div className='card border-success frx-auction-card'>
              <div className='card-header bg-success text-white'>
                <h6 className='mb-0'>
                  <i className='bi bi-box-arrow-in-left me-2'></i>Acquista
                  all'Asta
                </h6>
              </div>
              <div className='card-body'>
                <div className='mb-3'>
                  <select
                    className='form-select'
                    value={selectedWrestlerToBuy}
                    onChange={e => setSelectedWrestlerToBuy(e.target.value)}
                  >
                    <option value=''>Seleziona wrestler...</option>
                    {availableWrestlers.length > 0 ? (
                      availableWrestlers
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map(wrestler => (
                          <option key={wrestler.id} value={wrestler.id}>
                            {wrestler.name}
                          </option>
                        ))
                    ) : (
                      <option disabled>Nessun wrestler disponibile</option>
                    )}
                  </select>
                </div>

                {selectedWrestlerToBuy && (
                  <div className='mb-3'>
                    <div className='input-group'>
                      <span className='input-group-text'>$</span>
                      <input
                        type='number'
                        className='form-control'
                        value={auctionPrice}
                        onChange={e => setAuctionPrice(e.target.value)}
                        placeholder='Inserisci la tua offerta'
                        min='1'
                        required
                      />
                    </div>
                  </div>
                )}

                {selectedWrestlerToBuy && auctionPrice && (
                  <div className='alert alert-warning frx-alert bg-warning bg-opacity-10 text-light'>
                    <div className='d-flex justify-content-between'>
                      <span>Dopo l'acquisto ti resteranno:</span>
                      <strong>
                        {creditsAfterRelease - Number(auctionPrice)}$
                      </strong>
                    </div>
                  </div>
                )}

                <button
                  type='button'
                  className='btn btn-outline-success w-100'
                  onClick={handleBuyWrestler}
                  disabled={
                    !selectedWrestlerToBuy ||
                    !auctionPrice ||
                    creditsAfterRelease < Number(auctionPrice || 0)
                  }
                >
                  <i className='bi bi-box-arrow-in-left me-2'></i>Acquista
                  all'Asta
                </button>
              </div>
            </div>
          </div>

          {/* Tab Scambi */}
          <div
            className={`tab-pane fade ${
              activeTab === 'swap' ? 'show active' : ''
            }`}
          >
            <div className='card border-primary frx-auction-card'>
              <div className='card-header bg-primary text-white'>
                <h6 className='mb-0'>
                  <i className='bi bi-arrow-left-right me-2'></i>Scambia
                  Wrestler
                </h6>
              </div>
              <div className='card-body'>
                <SwapWrestler
                  factionId={factionId}
                  onSwapped={updatedFaction => {
                    // Aggiorna i dati locali con la fazione aggiornata
                    updateLocalData(updatedFaction)
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {message && (
          <div
            className={`alert ${
              message.includes('Errore') ? 'alert-danger' : 'alert-success'
            } frx-alert mt-3`}
          >
            {message}
          </div>
        )}
      </div>
    </div>
  )
}
